import React from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { AuthenticationResponse } from 'data/schemas/auth';
import { TeamCalendarEventUpsertRequest } from 'data/schemas/TeamCalendar';
import { toInputDateTimeValue } from './dateUtils';
import { MultiEmployeePicker } from './EmployeePicker';

export type TeamCalendarEventDraft = TeamCalendarEventUpsertRequest & {
    id?: string;
    createdOn?: string;
    updatedOn?: string;
};

interface Props {
    isOpen: boolean;
    draft: TeamCalendarEventDraft;
    employees: AuthenticationResponse[];
    availableTeams: string[];
    isEmployeesLoading?: boolean;
    isSaving: boolean;
    isReadOnly?: boolean;
    onClose: () => void;
    onChange: <K extends keyof TeamCalendarEventDraft>(
        key: K,
        value: TeamCalendarEventDraft[K]
    ) => void;
    onSubmit: () => void;
    onDelete?: () => void;
}

function EventModal(props: Props) {
    const {
        isOpen,
        draft,
        employees,
        availableTeams,
        isEmployeesLoading,
        isSaving,
        isReadOnly,
        onClose,
        onChange,
        onSubmit,
        onDelete
    } = props;

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
        >
            <ModalHeader toggle={onClose}>
                {draft.id ? (isReadOnly ? 'Calendar Event' : 'Edit Event') : 'Create Event'}
            </ModalHeader>
            <ModalBody>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <label className="block md:col-span-2">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Title</span>
                        <input
                            value={draft.title}
                            onChange={event => onChange('title', event.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                    <label className="block md:col-span-2">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Description</span>
                        <textarea
                            value={draft.description}
                            onChange={event => onChange('description', event.target.value)}
                            className="min-h-[100px] w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Start</span>
                        <input
                            type="datetime-local"
                            value={toInputDateTimeValue(draft.startAt)}
                            onChange={event => onChange('startAt', new Date(event.target.value).toISOString())}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">End</span>
                        <input
                            type="datetime-local"
                            value={toInputDateTimeValue(draft.endAt)}
                            onChange={event => onChange('endAt', new Date(event.target.value).toISOString())}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={draft.isAllDay}
                            onChange={event => onChange('isAllDay', event.target.checked)}
                            disabled={isReadOnly}
                        />
                        <span className="text-sm font-medium text-gray-700">All day</span>
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Owner name</span>
                        <input
                            value={draft.ownerName}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Owner citizen ID</span>
                        <input
                            value={draft.ownerCitizenId}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Who can view this?</span>
                        <select
                            value={draft.visibility}
                            onChange={event =>
                                onChange('visibility', event.target.value as TeamCalendarEventDraft['visibility'])
                            }
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        >
                            <option value="only_me">Only me</option>
                            <option value="selected_people">Selected people</option>
                            <option value="selected_teams">Selected teams</option>
                            <option value="all_menu_users">Everyone in menu</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Status</span>
                        <select
                            value={draft.status}
                            onChange={event => onChange('status', event.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        >
                            <option value="planned">Planned</option>
                            <option value="in-progress">In progress</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </label>
                    {draft.visibility === 'selected_people' && (
                        <label className="block md:col-span-2">
                            <span className="mb-1 block text-sm font-medium text-gray-700">
                                Selected viewers
                            </span>
                            <MultiEmployeePicker
                                values={draft.allowedViewerCitizenIds}
                                employees={employees}
                                disabled={isReadOnly || isEmployeesLoading}
                                placeholder={
                                    isEmployeesLoading
                                        ? 'Loading employees...'
                                        : 'Search and select employees'
                                }
                                onChange={citizenIds => onChange('allowedViewerCitizenIds', citizenIds)}
                            />
                        </label>
                    )}
                    {draft.visibility === 'selected_teams' && (
                        <label className="block md:col-span-2">
                            <span className="mb-1 block text-sm font-medium text-gray-700">
                                Selected teams
                            </span>
                            <div className="grid grid-cols-2 gap-2 rounded-md border border-gray-300 p-3 md:grid-cols-3">
                                {availableTeams.map(team => (
                                    <label
                                        key={team}
                                        className="flex items-center gap-2 text-sm text-gray-700"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={draft.allowedViewerTeams.includes(team)}
                                            onChange={event =>
                                                onChange(
                                                    'allowedViewerTeams',
                                                    event.target.checked
                                                        ? [...draft.allowedViewerTeams, team]
                                                        : draft.allowedViewerTeams.filter(
                                                              currentTeam => currentTeam !== team
                                                          )
                                                )
                                            }
                                            disabled={isReadOnly}
                                        />
                                        <span className="capitalize">{team}</span>
                                    </label>
                                ))}
                            </div>
                        </label>
                    )}
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Type</span>
                        <input
                            value={draft.eventType}
                            onChange={event => onChange('eventType', event.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">Color</span>
                        <input
                            type="color"
                            value={draft.color}
                            onChange={event => onChange('color', event.target.value)}
                            className="h-11 w-full rounded-md border border-gray-300 px-2 py-2"
                            disabled={isReadOnly}
                        />
                    </label>
                </div>
                <div className="mt-6 grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Created by
                        </div>
                        <div className="mt-1 text-sm text-gray-800">{draft.createdBy || '-'}</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Created on
                        </div>
                        <div className="mt-1 text-sm text-gray-800">{draft.createdOn || '-'}</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Updated by
                        </div>
                        <div className="mt-1 text-sm text-gray-800">{draft.updatedBy || '-'}</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Updated on
                        </div>
                        <div className="mt-1 text-sm text-gray-800">{draft.updatedOn || '-'}</div>
                    </div>
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        {draft.id && !isReadOnly && (
                            <button
                                type="button"
                                onClick={onDelete}
                                className="rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                        >
                            Close
                        </button>
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={onSubmit}
                                disabled={isSaving}
                                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        )}
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

export default EventModal;
